import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseStorage';
import { ProMaxModel, ProMaxModelStats, ProMaxAnalytics } from '../types/promax';
import { logAnalytics } from '../utils/database';

export const useProMaxModels = (activeOnly: boolean = true) => {
  const [models, setModels] = useState<ProMaxModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchModels = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('promax_models')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      setModels(data || []);
    } catch (err) {
      console.error('Error fetching ProMax models:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch models');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, [activeOnly]);

  return { models, loading, error, refetch: fetchModels };
};

export const useProMaxModelById = (modelId: string) => {
  const [model, setModel] = useState<ProMaxModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchModel = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('promax_models')
          .select('*')
          .eq('model_id', modelId)
          .maybeSingle();

        if (fetchError) {
          throw fetchError;
        }

        setModel(data);
      } catch (err) {
        console.error('Error fetching ProMax model:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch model');
      } finally {
        setLoading(false);
      }
    };

    if (modelId) {
      fetchModel();
    } else {
      setLoading(false);
    }
  }, [modelId]);

  return { model, loading, error };
};

export const createProMaxModel = async (
  modelData: Partial<ProMaxModel>,
  hyUserId: string
): Promise<{ success: boolean; data?: ProMaxModel; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from('promax_models')
      .insert([{
        ...modelData,
        hy_user_id: hyUserId
      }])
      .select()
      .single();

    if (error) {
      throw error;
    }

    await logAnalytics('promax_model_created', {
      model_id: data.model_id,
      model_name: data.name,
      creator_id: hyUserId,
      category: data.category,
      credit_cost: data.credit_cost
    });

    return { success: true, data };
  } catch (err) {
    console.error('Error creating ProMax model:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to create model'
    };
  }
};

export const updateProMaxModel = async (
  modelId: string,
  updates: Partial<ProMaxModel>
): Promise<{ success: boolean; data?: ProMaxModel; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from('promax_models')
      .update(updates)
      .eq('model_id', modelId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    await logAnalytics('promax_model_updated', {
      model_id: modelId,
      updates: Object.keys(updates)
    });

    return { success: true, data };
  } catch (err) {
    console.error('Error updating ProMax model:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to update model'
    };
  }
};

export const deleteProMaxModel = async (
  modelId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('promax_models')
      .delete()
      .eq('model_id', modelId);

    if (error) {
      throw error;
    }

    await logAnalytics('promax_model_deleted', {
      model_id: modelId
    });

    return { success: true };
  } catch (err) {
    console.error('Error deleting ProMax model:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to delete model'
    };
  }
};

export const toggleProMaxModelAvailability = async (
  modelId: string,
  currentStatus: boolean
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('promax_models')
      .update({ is_active: !currentStatus })
      .eq('model_id', modelId);

    if (error) {
      throw error;
    }

    await logAnalytics('promax_model_toggled', {
      model_id: modelId,
      new_status: !currentStatus
    });

    return { success: true };
  } catch (err) {
    console.error('Error toggling ProMax model availability:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to toggle model'
    };
  }
};

export const getProMaxModelStats = async (
  modelId?: string
): Promise<{ success: boolean; data?: ProMaxModelStats[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .rpc('get_popular_promax_models', { limit_count: modelId ? 1 : 50 });

    if (error) {
      throw error;
    }

    return { success: true, data: data || [] };
  } catch (err) {
    console.error('Error fetching ProMax model stats:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch stats'
    };
  }
};

export const logProMaxGeneration = async (
  analyticsData: Partial<ProMaxAnalytics>
): Promise<{ success: boolean; analytics_id?: string; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from('promax_analytics')
      .insert([analyticsData])
      .select('id')
      .single();

    if (error) {
      throw error;
    }

    return { success: true, analytics_id: data.id };
  } catch (err) {
    console.error('Error logging ProMax generation:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to log generation'
    };
  }
};

export const updateProMaxAnalytics = async (
  analyticsId: string,
  updates: Partial<ProMaxAnalytics>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('promax_analytics')
      .update(updates)
      .eq('id', analyticsId);

    if (error) {
      throw error;
    }

    return { success: true };
  } catch (err) {
    console.error('Error updating ProMax analytics:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to update analytics'
    };
  }
};

export const getUserProMaxHistory = async (
  hyUserId: string,
  limit: number = 50
): Promise<{ success: boolean; data?: any[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_promax_history', {
        target_user_id: hyUserId,
        limit_count: limit
      });

    if (error) {
      throw error;
    }

    return { success: true, data: data || [] };
  } catch (err) {
    console.error('Error fetching user ProMax history:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch history'
    };
  }
};
